import { TextField } from "@kora/kit/primitives/TextField/TextField";
import { useState } from "react";
import { Failure, GhostButton, Loading, Messages, Note, PrimaryButton, Screen } from "../components/Entry";
import { copy } from "../lib/copy";
import { errorsFor, nodeNamed, useFlow, valueOf } from "../lib/flow";
import { href, navigate } from "../lib/router";

export function Recovery({ query }: { query: URLSearchParams }) {
  const state = useFlow("recovery", "/recovery", query, { return_to: query.get("return_to") ?? undefined });
  const { flow } = state;
  const [typed, setEmail] = useState<string | null>(null);
  const [code, setCode] = useState("");
  if (!flow) return state.failure ? <Screen title="Reset password"><Failure text={state.failure} /></Screen> : <Loading />;

  const email = typed ?? (valueOf(flow, "email") || query.get("email") || "");
  const sent = flow.state === "sent_email";
  const byCode = Boolean(nodeNamed(flow, "code"));
  const method = byCode ? "code" : "link";
  const backToSignIn = () => navigate(href("/login", { return_to: flow.return_to }));

  return (
    <Screen title={sent ? "Check your email" : "Reset password"}>
      <form
        noValidate
        className="entry-form"
        onSubmit={event => {
          event.preventDefault();
          // A correct code makes Kratos send the browser on to settings to choose the new password.
          if (sent && byCode) void state.submit({ method, code }, () => undefined);
          else void state.submit({ method, email: email.trim() }, () => undefined);
        }}
      >
        {!sent && (
          <>
            <TextField
              label="Email"
              inputType="email"
              inputProps={{ autoComplete: "email", name: "email" }}
              value={email}
              onChange={setEmail}
              error={errorsFor(flow, "email").map(copy)[0]}
              size="lg"
            />
            {/* Never says whether an account exists. */}
            <Note>If an account exists, we’ll email a {byCode ? "recovery code" : "link to reset your password"}.</Note>
          </>
        )}
        {sent && byCode && (
          <>
            <Note>Enter the code for {email}.</Note>
            <TextField
              label="Recovery code"
              value={code}
              onChange={value => setCode(value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6, name: "code" }}
              error={errorsFor(flow, "code").map(copy)[0]}
              size="lg"
            />
          </>
        )}
        <Messages messages={flow.ui.messages ?? []} />
        <Failure text={state.failure} />
        {!sent && <PrimaryButton pending={state.pending}>{byCode ? "Send recovery code" : "Send reset link"}</PrimaryButton>}
        {sent && byCode && <PrimaryButton pending={state.pending}>Verify and continue</PrimaryButton>}
        {sent && (
          <GhostButton onClick={() => void state.submit({ method, email }, () => undefined)} disabled={state.pending}>
            {byCode ? "Send another code" : "Send another link"}
          </GhostButton>
        )}
        <GhostButton onClick={backToSignIn}>Back to sign in</GhostButton>
      </form>
    </Screen>
  );
}
